/**
 * Simulation browser component - Terminal aesthetic
 */

import { useState, useEffect } from 'react';
import { getSimulations } from '../services/api';
import type { Simulation } from '../types/simulation';
import { useNotification } from '../contexts/NotificationContext';
import './SimulationBrowser.css';

interface SimulationBrowserProps {
  onSelectSimulation: (simulation: Simulation) => void;
  setTitle: (title: string) => void;
}

export function SimulationBrowser({ onSelectSimulation, setTitle }: SimulationBrowserProps) {
  const { showNotification } = useNotification();

  useEffect(() => {
    setTitle('SIMULATION BROWSER');
  }, [setTitle]);

  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSimulations = async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    try {
      const data = await getSimulations();
      setSimulations(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load simulations');
      console.error('Failed to fetch simulations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSimulations(true);
    const interval = setInterval(fetchSimulations, 2000);
    return () => clearInterval(interval);
  }, []);


  const handleCopyLink = (e: React.MouseEvent, sim: Simulation) => {
    e.stopPropagation();
    const link = sim.sharing_link || (() => {
      const url = new URL(window.location.origin);
      url.searchParams.set('sim', sim.id);
      return url.toString();
    })();
    navigator.clipboard.writeText(link);
    showNotification('Link copied to clipboard!');
  };

  const handleCopySimId = (e: React.MouseEvent, simId: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(simId);
    showNotification('Simulation ID copied!');
  };

  return (
    <div className="simulation-browser">
      {isLoading && (
        <div className="browser-loading">
          <div className="loading-spinner"></div>
          <p>LOADING SIMULATIONS...</p>
        </div>
      )}

      {error && (
        <div className="browser-error">
          <p>ERROR: {error}</p>
          <button onClick={() => fetchSimulations(true)}>RETRY</button>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {simulations.length === 0 ? (
            <div className="browser-empty">
              <p>NO SIMULATIONS REGISTERED</p>
              <p className="empty-hint">Simulations appear here when a pocket server registers</p>
            </div>
          ) : (
            <div className="simulation-grid">
              {simulations.map((sim) => {
                const offline = sim.is_online === false;
                return (
                  <button
                    key={sim.id}
                    className={`simulation-card${offline ? ' simulation-card--offline' : ''}`}
                    onClick={() => !offline && onSelectSimulation(sim)}
                    aria-disabled={offline}
                    style={offline ? { cursor: 'not-allowed' } : undefined}
                  >
                    <button
                      className="card-share-btn"
                      onClick={(e) => handleCopySimId(e, sim.id)}
                      title="Copy simulation ID"
                      aria-label="Copy simulation ID"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                    <button
                      className="card-share-btn"
                      onClick={(e) => handleCopyLink(e, sim)}
                      title="Copy sharing link"
                      aria-label="Copy sharing link"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                        <polyline points="16 6 12 2 8 6"></polyline>
                        <line x1="12" y1="2" x2="12" y2="15"></line>
                      </svg>
                    </button>
                    <div className="card-content">
                      <h3 className="card-title">{sim.name}</h3>
                      {sim.description && <p className="card-description">{sim.description}</p>}
                      <div className="card-meta">
                        {sim.created_by != null ? (
                          <span className="card-owner">User #{sim.created_by}</span>
                        ) : (
                          <span className="card-owner card-owner--server">Server registered</span>
                        )}
                        {offline ? (
                          <span className="status-badge status-badge--offline">OFFLINE</span>
                        ) : sim.is_online === true && (
                          <span className="status-badge status-badge--active">ONLINE</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
